import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { 
  BarChart3, 
  TrendingUp, 
  Building2, 
  FileText, 
  Star, 
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  Users,
  Loader2,
  PieChart,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple Bar Chart Component
const SimpleBarChart = ({ data, dataKey, nameKey, color = '#3b82f6', height = 200 }) => {
  if (!data || data.length === 0) return <p className="text-muted-foreground text-sm">No data available</p>;
  
  const maxValue = Math.max(...data.map(d => d[dataKey]));
  
  return (
    <div className="space-y-2" style={{ height }}>
      {data.slice(0, 8).map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-24 truncate" title={item[nameKey]}>
            {item[nameKey]}
          </span>
          <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(item[dataKey] / maxValue) * 100}%`,
                backgroundColor: color
              }}
            />
          </div>
          <span className="text-xs font-medium w-8 text-right">{item[dataKey]}</span>
        </div>
      ))}
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, colors, size = 150 }) => {
  if (!data || Object.keys(data).length === 0) return null;
  
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-muted-foreground text-sm">No data</p>;
  
  let cumulative = 0;
  const segments = Object.entries(data).map(([key, value], index) => {
    const percentage = (value / total) * 100;
    const startAngle = cumulative * 3.6;
    cumulative += percentage;
    return { key, value, percentage, startAngle, color: colors[index % colors.length] };
  });
  
  return (
    <div className="flex items-center gap-4">
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" className="transform -rotate-90">
          {segments.map((seg, i) => {
            const strokeDasharray = `${seg.percentage * 2.51} 251`;
            const strokeDashoffset = -segments.slice(0, i).reduce((a, s) => a + s.percentage * 2.51, 0);
            return (
              <circle
                key={seg.key}
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={seg.color}
                strokeWidth="20"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold">{total}</span>
        </div>
      </div>
      <div className="space-y-1">
        {segments.map(seg => (
          <div key={seg.key} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
            <span className="capitalize">{seg.key.replace('_', ' ')}</span>
            <span className="font-medium">({seg.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Analytics = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState('30d');
  const [topSortBy, setTopSortBy] = useState('rating');

  const fetchAnalytics = useCallback(async () => {
    try {
      const [overviewRes, trendsRes, topRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/trends?period=${trendPeriod}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/top-suppliers?sort_by=${topSortBy}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setTopSuppliers(topRes.data.suppliers);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, trendPeriod, topSortBy]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const statusColors = ['#22c55e', '#eab308', '#f97316', '#ef4444', '#6b7280'];
  const sectorColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#10b981', '#f43f5e'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-1">Insights and performance metrics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="total-suppliers-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Suppliers</p>
                <p className="text-3xl font-bold">{overview?.summary?.total_suppliers || 0}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="total-documents-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Documents</p>
                <p className="text-3xl font-bold">{overview?.summary?.total_documents || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="compliance-rate-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Compliance Rate</p>
                <p className="text-3xl font-bold">{overview?.summary?.compliance_rate || 0}%</p>
              </div>
              <Shield className="w-8 h-8 text-green-500 opacity-50" />
            </div>
            <Progress value={overview?.summary?.compliance_rate || 0} className="mt-2 h-1" />
          </CardContent>
        </Card>

        <Card data-testid="avg-rating-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Rating</p>
                <div className="flex items-baseline gap-1">
                  <p className="text-3xl font-bold">{overview?.summary?.avg_rating || 0}</p>
                  <span className="text-sm text-muted-foreground">/5</span>
                </div>
              </div>
              <Star className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiry Forecast */}
      <Card data-testid="expiry-forecast-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Document Expiry Forecast
          </CardTitle>
          <CardDescription>Documents expiring in the coming months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-3xl font-bold text-yellow-600">{overview?.expiry_forecast?.next_30_days || 0}</p>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">Next 30 Days</p>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <p className="text-3xl font-bold text-orange-600">{overview?.expiry_forecast?.next_60_days || 0}</p>
              <p className="text-sm text-orange-700 dark:text-orange-400">Next 60 Days</p>
            </div>
            <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <p className="text-3xl font-bold text-red-600">{overview?.expiry_forecast?.next_90_days || 0}</p>
              <p className="text-sm text-red-700 dark:text-red-400">Next 90 Days</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Distribution Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card data-testid="status-distribution-card">
          <CardHeader>
            <CardTitle className="text-lg">Supplier Status</CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart 
              data={overview?.status_distribution} 
              colors={statusColors}
              size={140}
            />
          </CardContent>
        </Card>

        {/* Compliance Distribution */}
        <Card data-testid="compliance-distribution-card">
          <CardHeader>
            <CardTitle className="text-lg">Compliance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart 
              data={{
                compliant: overview?.compliance?.compliant || 0,
                warning: overview?.compliance?.warning || 0,
                non_compliant: overview?.compliance?.non_compliant || 0
              }} 
              colors={['#22c55e', '#eab308', '#ef4444']}
              size={140}
            />
          </CardContent>
        </Card>
      </div>

      {/* Sector & Area Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="sector-distribution-card">
          <CardHeader>
            <CardTitle className="text-lg">Top Sectors</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleBarChart 
              data={overview?.sector_distribution || []} 
              dataKey="count" 
              nameKey="name"
              color="#3b82f6"
            />
          </CardContent>
        </Card>

        <Card data-testid="area-distribution-card">
          <CardHeader>
            <CardTitle className="text-lg">Top Areas</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleBarChart 
              data={overview?.area_distribution || []} 
              dataKey="count" 
              nameKey="name"
              color="#8b5cf6"
            />
          </CardContent>
        </Card>
      </div>

      {/* Document Type Distribution */}
      <Card data-testid="document-type-card">
        <CardHeader>
          <CardTitle className="text-lg">Document Types</CardTitle>
        </CardHeader>
        <CardContent>
          <SimpleBarChart 
            data={overview?.document_type_distribution || []} 
            dataKey="count" 
            nameKey="type"
            color="#14b8a6"
          />
        </CardContent>
      </Card>

      {/* Top Suppliers */}
      <Card data-testid="top-suppliers-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Top Suppliers</CardTitle>
            <Select value={topSortBy} onValueChange={setTopSortBy}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">By Rating</SelectItem>
                <SelectItem value="documents">By Documents</SelectItem>
                <SelectItem value="compliance">By Compliance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {topSuppliers.length === 0 ? (
            <p className="text-muted-foreground text-sm">No suppliers found</p>
          ) : (
            <div className="space-y-3">
              {topSuppliers.slice(0, 5).map((supplier, index) => (
                <Link 
                  key={supplier.supplier_id} 
                  to={`/suppliers/${supplier.supplier_id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium">{supplier.company_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {supplier.sectors?.slice(0, 2).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {topSortBy === 'rating' && supplier.average_rating && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        {supplier.average_rating}
                      </Badge>
                    )}
                    {topSortBy === 'documents' && supplier.doc_count !== undefined && (
                      <Badge variant="outline">{supplier.doc_count} docs</Badge>
                    )}
                    {topSortBy === 'compliance' && supplier.manually_compliant && (
                      <Badge className="bg-green-100 text-green-700">Verified</Badge>
                    )}
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
